import {useEffect, useState} from 'react';
import {useMutation} from '@apollo/client';
import {Button} from '@dabafinance/react-components';
import {Dropdown, Form, Select, Switch} from 'antd';
import {toast} from 'react-toastify';
import DBModal from 'components/modal/modal';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {ConfirmModal} from 'pages/adminOpportunities/stocksListings/ViewOrders/HandleOrders';
import {
  DELETE_FINANCIAL_INSTRUMENT,
  LIST_IPO,
  UPDATE_BOND_MATURITY_STATUS,
  UPDATE_FINANCIAL_INSTRUMENT_STATUS,
} from 'graphql/mutations/assetManagement';
import {assetClassStatus} from 'pages/AssetManagement/modules/constants';
import {instrumentSupports} from 'pages/AssetManagement/modules/instrumentKeys';
import {formatEnumType} from 'utils/helper';
import shell from 'pages/AssetManagement/v2/pageShell.module.scss';
import {AssetValuationModal} from './AssetValuationModal';
import {InstrumentOrders} from './InstrumentOrders';
import {InventoryActivity} from './inventory/InventoryActivity';
import inventoryActivityStyles from './inventory/inventoryActivity.module.scss';
import {
  InstrumentInventory,
  InventoryMovementModal,
} from './inventory/InstrumentInventory';

/**
 * The per-row Actions menu for a single financial instrument, with every modal
 * it opens.
 *
 * Lives here rather than in one screen because two lists show the same rows:
 * the asset-class instruments list and the Multi Asset → Instruments
 * workspace. They were drifting — the workspace had no row menu at all, so an
 * operator who reached an instrument through the workspace could not update a
 * status, book a movement or read its orders without first finding the same
 * row under its asset class.
 *
 * `refetch` is whatever reloads the calling list; `instrumentKey` decides which
 * groups appear, since maturity, valuation and inventory are not offered on
 * every type.
 */
export const useInstrumentRowActions = ({
  instrumentKey,
  pageOperations,
  refetch,
  navigate,
}) => {
  const [statusModal, setStatusModal] = useState({
    id: null,
    open: false,
    status: '',
  });
  const [openMaturityModal, setOpenMaturityModal] = useState({
    open: false,
    content: null,
  });
  const [openOrdersModal, setOpenOrdersModal] = useState({
    open: false,
    instrumentId: null,
  });
  const [openValuationModal, setOpenValuationModal] = useState({
    open: false,
    content: null,
  });
  const [openInventoryModal, setOpenInventoryModal] = useState({
    open: false,
    instrumentId: null,
    inventory: null,
  });
  const [openInventoryMovementModal, setOpenInventoryMovementModal] = useState({
    open: false,
    id: null,
  });
  const [activityInstrument, setActivityInstrument] = useState(null);
  const [deleteModal, setDeleteModal] = useState({id: null, open: false});
  const [listIpoModal, setListIpoModal] = useState({
    open: false,
    content: null,
  });
  // Second press required before a maturity flag that destroys data is sent.
  const [maturityConfirmed, setMaturityConfirmed] = useState(false);

  const [statusForm] = Form.useForm();
  const [maturityForm] = Form.useForm();
  const [inventoryForm] = Form.useForm();
  const [inventoryMovementForm] = Form.useForm();

  const [deleteFinancialInstrument, {loading: loadingDelete}] = useMutation(
    DELETE_FINANCIAL_INSTRUMENT,
  );
  const [updateFinancialInstrumentStatus, {loading: loadingStatusUpdate}] =
    useMutation(UPDATE_FINANCIAL_INSTRUMENT_STATUS);
  const [updateMaturityStatus, {loading: loadingMaturityUpdate}] = useMutation(
    UPDATE_BOND_MATURITY_STATUS,
  );
  // Graduating an allocated IPO into a tradeable equity. Nothing else in admin
  // exposes this, so an allocated IPO otherwise never becomes a stock (§14c).
  const [listIpo, {loading: loadingListIpo}] = useMutation(LIST_IPO);

  useEffect(() => {
    statusForm.setFieldsValue({status: statusModal.status});
  }, [statusModal.open]);

  useEffect(() => {
    maturityForm.setFieldsValue({
      matured: openMaturityModal?.content?.marketPhase === 'MATURED',
    });
  }, [openMaturityModal.open]);

  const actionsColumn = {
    title: 'Actions',
    dataIndex: 'actions',
    key: 'actions',
    width: 120,
    render: (text, record) => (
      <Dropdown
        trigger="click"
        menu={{
          items: [
            {
              key: 'instrument',
              label: 'Instrument',
              type: 'group',
              children: [
                {
                  key: 3,
                  label: 'Update Status',
                  disabled: !pageOperations?.update,
                  onClick: e => {
                    e.domEvent.stopPropagation();
                    setStatusModal({
                      id: record?.id,
                      open: true,
                      status: record?.status,
                    });
                  },
                },
                ...(instrumentKey === 'IPO'
                  ? [
                      {
                        key: 'list-ipo',
                        label: record?.listedInstrumentId
                          ? 'Already listed'
                          : 'List IPO as equity',
                        disabled:
                          !pageOperations?.update ||
                          !!record?.listedInstrumentId,
                        onClick: e => {
                          e.domEvent.stopPropagation();
                          setListIpoModal({open: true, content: record});
                        },
                      },
                    ]
                  : []),
                ...(instrumentSupports(instrumentKey, 'maturity')
                  ? [
                      {
                        key: 'maturity',
                        label: 'Update Maturity Status',
                        disabled: !pageOperations?.update,
                        onClick: e => {
                          e.domEvent.stopPropagation();
                          setOpenMaturityModal({open: true, content: record});
                        },
                      },
                    ]
                  : []),
                ...(instrumentSupports(instrumentKey, 'valuation')
                  ? [
                      {
                        key: 'valuation',
                        label: 'Update Instrument Valuation',
                        disabled: !pageOperations?.update,
                        onClick: e => {
                          e.domEvent.stopPropagation();
                          setOpenValuationModal({
                            open: true,
                            content: record,
                          });
                        },
                      },
                    ]
                  : []),
                {
                  key: 2,
                  label: 'Delete',
                  disabled: !pageOperations?.delete,
                  onClick: e => {
                    e.domEvent.stopPropagation();
                    setDeleteModal({id: record?.id, open: true});
                  },
                },
              ],
            },
            {
              key: 'orders',
              label: 'Orders',
              type: 'group',
              children: [
                {
                  key: 5,
                  label: 'View Orders',
                  disabled: !pageOperations?.view,
                  onClick: e => {
                    e.domEvent.stopPropagation();
                    setOpenOrdersModal({
                      open: true,
                      instrumentId: record?.id,
                      name: record?.name,
                    });
                  },
                },
                {
                  key: 6,
                  label: 'View Orders on Trading View',
                  disabled: !pageOperations?.view,
                  onClick: e => {
                    e.domEvent.stopPropagation();
                    navigate(
                      `/dashboard/stocks/listings?route=order&instrumentId=${record?.id}`,
                    );
                  },
                },
              ],
            },

            ...(instrumentSupports(instrumentKey, 'inventory')
              ? [
                  {
                    key: 'inventory',
                    label: 'Inventory',
                    type: 'group',
                    children: [
                      {
                        key: 7,
                        label: 'View Inventory',
                        // Nothing to show until a movement opens a position.
                        disabled:
                          !pageOperations?.view || !record?.inventoryPosition,
                        onClick: e => {
                          e.domEvent.stopPropagation();
                          setOpenInventoryModal({
                            open: true,
                            instrumentId: record?.id,
                            inventory: record?.inventoryPosition,
                          });
                        },
                      },
                      {
                        key: 'inventory-activity',
                        label: 'Inventory Activity',
                        disabled: !pageOperations?.view,
                        onClick: e => {
                          e.domEvent.stopPropagation();
                          setActivityInstrument({
                            id: record.id,
                            name: record.name || record.title,
                            ticker: record.ticker,
                            currency: record.currency?.currencyCode,
                          });
                        },
                      },
                      {
                        key: 8,
                        label: 'Configure Inventory Settings',
                        // `configureInventoryPosition` takes `id: ID!` — there
                        // has to be a position to configure.
                        disabled:
                          !pageOperations?.update || !record?.inventoryPosition,
                        onClick: e => {
                          e.domEvent.stopPropagation();
                          setOpenInventoryModal({
                            open: true,
                            instrumentId: record?.id,
                            type: 'enable',
                            currency: record?.currency?.currencyCode,
                            inventory: record?.inventoryPosition,
                            isTradingEnabled:
                              record?.inventoryPosition?.enabledForTrading,
                          });
                        },
                      },
                      {
                        key: 9,
                        label: record?.inventoryPosition
                          ? 'Create Inventory Movement'
                          : 'Open Inventory With a Movement',
                        /**
                         * Was gated on an existing `inventoryPosition`, which
                         * made inventory impossible to start: every action in
                         * this group needed a position and nothing here could
                         * create one.
                         *
                         * `createInventoryMovement` is keyed on the instrument
                         * — its arguments are `input`, `instrumentId` and
                         * `instrumentKey`, with no position id anywhere — so
                         * booking the first inbound movement is precisely how
                         * a position comes into being. Only the permission
                         * still gates it.
                         */
                        disabled: !pageOperations?.update,
                        onClick: e => {
                          e.domEvent.stopPropagation();
                          setOpenInventoryMovementModal({
                            open: true,
                            currency: record?.currency?.currencyCode,
                            instrumentId: record?.id,
                            // The whole position, so the confirmation step
                            // can say what the balance becomes.
                            position: record?.inventoryPosition,
                          });
                        },
                      },
                    ],
                  },
                ]
              : []),
          ],
        }}>
        <div onClick={e => e.stopPropagation()}>
          <Button type="secondary" label="Actions" />
        </div>
      </Dropdown>
    ),
  };

  const actionModals = (
    <>
      <ConfirmModal
        openModal={deleteModal.open}
        loading={loadingDelete}
        title="Delete Instrument"
        confirmText="Are you sure you want to delete this instrument? This action cannot be undone."
        setOpenModal={() => setDeleteModal({id: null, open: false})}
        handleOperation={() =>
          deleteFinancialInstrument({
            variables: {
              deleteFinancialInstrumentId: deleteModal.id,
            },
          }).then(({data: {deleteFinancialInstrument}}) => {
            if (deleteFinancialInstrument.__typename === 'Error') {
              toast.error(deleteFinancialInstrument.message);
            } else {
              toast.success('Instrument Deleted');
              setDeleteModal({id: null, open: false});
              refetch();
            }
          })
        }
      />
      <DBModal
        isOpen={openValuationModal.open}
        handleClose={() => setOpenValuationModal({open: false, data: null})}
        width={600}
        content={
          <AssetValuationModal
            refetch={refetch}
            content={openValuationModal.content}
            onClose={() => setOpenValuationModal({open: false, data: null})}
          />
        }
      />
      <DBModal
        isOpen={statusModal.open}
        handleClose={() => {
          statusForm.resetFields();
          setStatusModal({id: null, open: false, status: ''});
        }}
        content={
          <div className="confirm-modal">
            <div>
              <h1>Update Instrument Status</h1>
              <p>Are you sure you want to update this status</p>
            </div>
            <Form
              form={statusForm}
              onFinish={values =>
                updateFinancialInstrumentStatus({
                  variables: {
                    updateFinancialInstrumentStatusId: statusModal.id,
                    status: values.status,
                  },
                }).then(({data: {updateFinancialInstrumentStatus}}) => {
                  if (updateFinancialInstrumentStatus.__typename === 'Error') {
                    toast.error(updateFinancialInstrumentStatus.message);
                  } else {
                    toast.success(
                      `Instrument status updated to ${formatEnumType(
                        values.status,
                      )}`,
                    );
                    setStatusModal({id: null, open: false, status: ''});
                    refetch();
                    statusForm.resetFields();
                  }
                })
              }
              layout="vertical">
              <Form.Item name="status" label="Status">
                <Select>
                  {assetClassStatus.map(data => (
                    <Select.Option key={data.value}>{data.label}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <SubmitButton
                disabled={loadingStatusUpdate}
                label="Update Status"
              />
            </Form>
          </div>
        }
      />
      <DBModal
        isOpen={openMaturityModal.open}
        handleClose={() => {
          maturityForm.resetFields();
          setMaturityConfirmed(false);
          setOpenMaturityModal({open: false, content: null});
        }}
        content={
          <div className="confirm-modal">
            <div style={{textAlign: 'center', marginBottom: '24px'}}>
              <h1>Update Maturity Status</h1>
              <p style={{color: '#666', fontSize: '14px', marginTop: '12px'}}>
                Indicates whether the bond has reached its maturity date and
                principal repayment is due. When set to true, all portfolio
                records are removed and certificates are destroyed.
              </p>
            </div>
            <Form
              form={maturityForm}
              onFinish={values => {
                const matured = values.matured ?? false;
                // Marking a bond matured deletes portfolio records and
                // destroys certificates, as the copy above says. That is not
                // something to do on one click of a toggle.
                if (matured && !maturityConfirmed) {
                  setMaturityConfirmed(true);
                  return;
                }
                updateMaturityStatus({
                  variables: {
                    instrumentId: openMaturityModal?.content?.id,
                    matured,
                  },
                })
                  .then(({data}) => {
                    const result = data?.updateBondMaturityStatus;
                    if (result?.__typename === 'Error') {
                      toast.error(result.message);
                      // The write may still have gone through, so show the
                      // real state rather than leaving a stale row.
                      refetch();
                      return;
                    }
                    toast.success('Maturity Status Updated');
                    setOpenMaturityModal({open: false, content: null});
                    refetch();
                    maturityForm.resetFields();
                    setMaturityConfirmed(false);
                  })
                  .catch(error => {
                    /**
                     * There was no catch here at all, so a throw left the
                     * modal open with no feedback — and this mutation can
                     * throw *after* committing (the gateway currently answers
                     * "Cannot call abortTransaction after calling
                     * commitTransaction" on a write that succeeded). Refetch
                     * so the list reflects what actually happened, and never
                     * repeat a database driver's words back to an operator.
                     */
                    // eslint-disable-next-line no-console
                    console.error('updateBondMaturityStatus failed', error);
                    toast.error(
                      'The maturity update did not complete cleanly. The list has been refreshed — check whether it applied before trying again.',
                    );
                    refetch();
                    setOpenMaturityModal({open: false, content: null});
                    maturityForm.resetFields();
                    setMaturityConfirmed(false);
                  });
              }}
              layout="vertical">
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginBottom: '24px',
                }}>
                <span style={{marginRight: '12px', fontSize: '16px'}}>
                  Is this Instrument Matured?
                </span>
                <Form.Item name="matured" valuePropName="checked" noStyle>
                  <Switch />
                </Form.Item>
              </div>
              {maturityConfirmed && (
                <p className={shell.destructiveNote}>
                  This removes every portfolio record for this bond and destroys
                  its certificates. It cannot be undone.
                </p>
              )}
              <SubmitButton
                disabled={loadingMaturityUpdate}
                label={maturityConfirmed ? 'Yes, mark it matured' : 'Update'}
              />
            </Form>
          </div>
        }
      />
      <ConfirmModal
        openModal={listIpoModal.open}
        setOpenModal={() => setListIpoModal({open: false, content: null})}
        loading={loadingListIpo}
        title="List this IPO as an equity"
        confirmText={`This graduates "${
          listIpoModal.content?.name ?? ''
        }" into a tradeable public equity. The IPO keeps its record and links to the new instrument. This cannot be undone from admin.`}
        handleOperation={() =>
          listIpo({variables: {instrumentId: listIpoModal.content?.id}})
            .then(({data: {listIpo: res}}) => {
              if (res?.__typename === 'Error') {
                toast.error(res?.message);
              } else {
                toast.success('IPO listed as a public equity');
                setListIpoModal({open: false, content: null});
                refetch();
              }
            })
            .catch(error => toast.error(error.message))
        }
      />
      <DBModal
        isOpen={openOrdersModal.open}
        handleClose={() =>
          setOpenOrdersModal({open: false, instrumentId: null})
        }
        width={1100}
        content={
          <InstrumentOrders
            instrumentId={openOrdersModal.instrumentId}
            name={openOrdersModal.name}
          />
        }
      />
      <DBModal
        isOpen={openInventoryModal.open}
        handleClose={() => {
          inventoryForm.resetFields();
          setOpenInventoryModal({
            open: false,
            instrumentId: null,
            isTradingEnabled: null,
            inventory: null,
          });
        }}
        content={
          <InstrumentInventory
            instrumentId={openInventoryModal.instrumentId}
            form={inventoryForm}
            isTradingEnabled={openInventoryModal.isTradingEnabled}
            refetch={refetch}
            open={openInventoryModal}
            type={openInventoryModal.type}
            inventory={openInventoryModal.inventory}
            currency={openInventoryModal.currency}
            onClose={() => {
              inventoryForm.resetFields();
              setOpenInventoryModal({
                open: false,
                instrumentId: null,
                currency: null,
                isTradingEnabled: null,
                inventory: null,
              });
            }}
          />
        }
      />
      <DBModal
        isOpen={openInventoryMovementModal.open}
        // Remount on every open so a half-finished movement is never waiting
        // on the review step when the modal is opened for a different row.
        destroyOnClose
        handleClose={() => {
          inventoryMovementForm.resetFields();
          setOpenInventoryMovementModal({open: false});
        }}
        content={
          <InventoryMovementModal
            form={inventoryMovementForm}
            refetch={refetch}
            instrumentId={openInventoryMovementModal.instrumentId}
            instrumentKey={instrumentKey}
            position={openInventoryMovementModal.position}
            currency={openInventoryMovementModal.currency}
            id={openInventoryMovementModal.id}
            onClose={() => {
              inventoryMovementForm.resetFields();
              setOpenInventoryMovementModal({open: false, id: null});
            }}
          />
        }
      />
      <DBModal
        isOpen={!!activityInstrument}
        destroyOnClose
        width={820}
        className={inventoryActivityStyles.modal}
        handleClose={() => setActivityInstrument(null)}
        content={
          activityInstrument ? (
            <InventoryActivity
              key={activityInstrument.id}
              instrumentId={activityInstrument.id}
              instrumentName={activityInstrument.name}
              ticker={activityInstrument.ticker}
              currency={activityInstrument.currency}
            />
          ) : null
        }
      />
    </>
  );

  return {actionsColumn, actionModals};
};
